import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { 
  LuxuryPhone as Phone, 
  LuxuryMail as Mail, 
  LuxuryMapPin as MapPin, 
  LuxuryClock as Clock,
  LuxuryInstagram as Instagram,
  LuxuryFacebook as Facebook,
  LuxuryTwitter as Twitter,
  LuxuryLinkedin as Linkedin,
  LuxuryHeart as Heart,
  LuxuryStar as Star,
  LuxuryShield as Shield,
  LuxuryAward as Award
} from './icons/LuxuryIcons';

const Footer = () => {
  const navigate = useNavigate();

  const footerSections = {
    destinations: {
      title: 'Destinations',
      links: [
        { name: 'France', path: '/search?region=france' },
        { name: 'Italie', path: '/search?region=italie' },
        { name: 'Grèce', path: '/search?region=grece' },
        { name: 'Espagne', path: '/search?region=espagne' },
        { name: 'Caraïbes', path: '/search?region=caraibes' },
        { name: 'Toutes destinations', path: '/search' }
      ]
    },
    privia: {
      title: 'PRIVIA',
      links: [
        { name: 'À propos', path: '/about' },
        { name: 'Notre différence', path: '/our-difference' },
        { name: 'Inspirations', path: '/inspirations' },
        { name: 'Presse', path: '/press' },
        { name: 'Carrières', path: '/careers' },
        { name: 'Partenaires', path: '/partners' }
      ]
    },
    services: {
      title: 'Services',
      links: [
        { name: 'Conciergerie 24/7', path: '/conciergerie' },
        { name: 'Expériences sur-mesure', path: '/experiences' },
        { name: 'Événementiel privé', path: '/events' },
        { name: 'Yacht & jets privés', path: '/luxury-transport' },
        { name: 'Chef à domicile', path: '/chef-service' },
        { name: 'Spa & bien-être', path: '/wellness' }
      ]
    },
    support: {
      title: 'Support',
      links: [
        { name: 'Conditions générales', path: '/terms' },
        { name: 'Politique de confidentialité', path: '/privacy' },
        { name: 'Assurance voyage', path: '/insurance' },
        { name: 'Politique d\'annulation', path: '/cancellation' },
        { name: 'Nous contacter', path: '/contact' }
      ]
    }
  };

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/privia' },
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/privia' },
    { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/privia' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/privia' }
  ];

  const trustIndicators = [
    { icon: Shield, text: 'Paiements sécurisés' },
    { icon: Award, text: 'Service certifié' },
    { icon: Star, text: '98% satisfaction' },
    { icon: Heart, text: 'Support 24/7' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <h2 
              className="text-3xl font-light mb-6 tracking-wide"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              PRIVIA
            </h2>
            <p 
              className="text-gray-400 mb-6 leading-relaxed text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              L'incarnation visuelle des émotions les plus raffinées. Depuis 2014, nous créons des expériences de voyage inoubliables.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Phone className="w-4 h-4 text-amber-500" />
                <span>+1 786 840 0745</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Mail className="w-4 h-4 text-amber-500" />
                <span>chris@priviacollection.com</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Clock className="w-4 h-4 text-amber-500" />
                <span>Mon-Sat 7:30am-7:30pm EST</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-amber-600 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                  >
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key} className="lg:col-span-1">
              <h3 
                className="text-lg font-semibold mb-6 text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-gray-400 hover:text-amber-400 text-sm transition-colors duration-300 text-left"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustIndicators.map((indicator, index) => {
              const IconComponent = indicator.icon;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-600/20 rounded-full flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-amber-500" />
                  </div>
                  <span className="text-sm text-gray-400">{indicator.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <span>© 2024 PRIVIA. Tous droits réservés.</span>
              <button className="hover:text-amber-400 transition-colors duration-300">
                Mentions légales
              </button>
              <button className="hover:text-amber-400 transition-colors duration-300">
                RGPD
              </button>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>Conçu avec</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>à Paris</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;